import React, { useEffect, useMemo, lazy, Suspense } from 'react';
import Footer from '@/components/Footer';
import { useParams, Navigate } from 'react-router-dom';
import { getToolBySlug } from '@/data/tools';
import { getRedirectTarget, isNoindex, getCanonicalOverride } from '@/data/seo/urlClassification';
import NavBar from '@/components/NavBar';
import SEOHead from '@/components/SEOHead';
import BreadcrumbSEO from '@/components/BreadcrumbSEO';
import ImageUploader from '@/components/ImageUploader';
import ToolExample from '@/components/ToolExample';
import RelatedTools from '@/components/RelatedTools';
import NotFound from '@/pages/NotFound';
import { CheckCircle, HelpCircle, Loader } from 'lucide-react';
import { useAnalytics } from '@/hooks/useAnalytics';
import { useIsMobile } from '@/hooks/use-mobile';

const ImageResizer = lazy(() => import('@/components/ImageResizer'));
const TargetSizeTemplate = lazy(() => import('@/components/TargetSizeTemplate'));
const TargetSizeCompressor = lazy(() => import('@/components/TargetSizeCompressor'));
const FormatConverter = lazy(() => import('@/components/FormatConverter'));
const ImageCropper = lazy(() => import('@/components/ImageCropper'));
const ImageRotator = lazy(() => import('@/components/ImageRotator'));
const ImageResizerPercent = lazy(() => import('@/components/ImageResizerPercent'));
const BatchProcessor = lazy(() => import('@/components/BatchProcessor'));
const ImageAdjuster = lazy(() => import('@/components/ImageAdjuster'));
const ImageToPdf = lazy(() => import('@/components/ImageToPdf'));
const AdSlot = lazy(() => import('@/components/AdSlot'));

const ToolPage = () => {
  const { slug } = useParams<{ slug: string }>();

  // ── SEO classification (computed before hooks, no conditional logic) ──
  const redirectTarget = slug ? getRedirectTarget(slug) : undefined;
  const tool = slug && !redirectTarget ? getToolBySlug(slug) : undefined;
  const shouldNoindex = slug ? isNoindex(slug) : false;
  const canonicalOverride = slug ? getCanonicalOverride(slug) : undefined;

  const { trackEvent } = useAnalytics();
  const isMobile = useIsMobile();

  // Memoize FAQ structured data based on actual content
  const faqStructuredData = useMemo(() => {
    if (!tool?.faqItems || tool.faqItems.length === 0) return null;
    return {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": tool.faqItems.map(faq => ({
        "@type": "Question",
        "name": faq.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.answer,
        },
      })),
    };
  }, [tool?.faqItems]);

  useEffect(() => {
    if (tool) {
      trackEvent('page_view', { page: `/herramientas/${tool.slug}`, page_type: 'tool_page', tool_slug: tool.slug, device_type: isMobile ? 'mobile' : 'desktop' });
    }
  }, [tool?.slug]);

  // Inject FAQPage JSON-LD into head with cleanup
  useEffect(() => {
    if (!faqStructuredData || !tool) return;

    // Remove any prior faq-structured-data-* scripts (stale from other tool pages)
    document.querySelectorAll('script[id^="faq-structured-data-"]').forEach(el => el.remove());

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = `faq-structured-data-${tool.slug}`;
    script.textContent = JSON.stringify(faqStructuredData);
    document.head.appendChild(script);

    return () => {
      const el = document.getElementById(`faq-structured-data-${tool.slug}`);
      if (el) el.remove();
    };
  }, [faqStructuredData, tool?.slug]);

  // ── SEO: Client-side redirect for synonym/duplicate slugs ──
  // PRODUCTION NOTE: Server-side 301 redirects recommended (Vercel/Netlify config)
  if (redirectTarget) {
    const target = redirectTarget.startsWith('/')
      ? redirectTarget
      : `/herramientas/${redirectTarget}`;
    return <Navigate to={target} replace />;
  }

  if (!tool) return <NotFound />;

  // Read metadata directly from tool definition
  const sourceFormat = tool.sourceFormat;
  const targetFormat = tool.targetFormat;
  const targetSize = tool.targetSize;

  // Ad configuration per tool
  const showAds = tool.enableAds !== false;
  const adPositions = tool.adPositions ?? ['header', 'result'];

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={tool.metaTitle}
        description={tool.metaDescription}
        canonical={canonicalOverride ? `https://compresorjpeg.com${canonicalOverride}` : `https://compresorjpeg.com${tool.canonicalPath}`}
        noindex={shouldNoindex}
      />

      <NavBar />

      <main className="container mx-auto px-4 py-6">
        <BreadcrumbSEO
          items={[
            { label: 'Inicio', href: '/' },
            { label: 'Herramientas', href: '/herramientas' },
            { label: tool.title },
          ]}
        />

        <section className="text-center py-8 max-w-3xl mx-auto">
          <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            {tool.h1}
          </h1>
          <p className="text-lg text-muted-foreground">
            {tool.subtitle}
          </p>
        </section>

        {/* Header Ad — below title, above tool */}
        {showAds && adPositions.includes('header') && (
          <Suspense fallback={null}>
            <AdSlot position="header" className="max-w-3xl mx-auto" />
          </Suspense>
        )}

        <section className="mb-8">
          <Suspense fallback={
            <div className="flex items-center justify-center py-12">
              <Loader className="w-8 h-8 text-primary animate-spin" />
            </div>
          }>
            {(() => {
              switch (tool.pageType) {
                case 'resize':
                  return <ImageResizer tool={tool} enableAds={showAds} adPositions={adPositions} />;
                case 'target-size':
                  // Use new template for pure target-size pages (reducir-imagen-*, reducir-foto-*)
                  // Exclude use-case hybrids like reducir-imagen-para-cv
                  if (tool.slug.match(/^reducir-(imagen|foto)-\d+[a-z]+$/i)) {
                    return <TargetSizeTemplate tool={tool} enableAds={showAds} adPositions={adPositions} />;
                  }
                  return <TargetSizeCompressor tool={tool} enableAds={showAds} adPositions={adPositions} />;
                case 'conversion':
                  return <FormatConverter tool={tool} enableAds={showAds} adPositions={adPositions} />;
                case 'crop':
                  return <ImageCropper tool={tool} enableAds={showAds} adPositions={adPositions} />;
                case 'rotate':
                  return <ImageRotator tool={tool} enableAds={showAds} adPositions={adPositions} />;
                case 'resize-percent':
                  return <ImageResizerPercent tool={tool} enableAds={showAds} adPositions={adPositions} />;
                case 'batch':
                  return <BatchProcessor tool={tool} enableAds={showAds} adPositions={adPositions} />;
                case 'adjust':
                  return <ImageAdjuster tool={tool} enableAds={showAds} adPositions={adPositions} />;
                case 'image-to-pdf':
                  return <ImageToPdf tool={tool} enableAds={showAds} adPositions={adPositions} />;
                default:
                  return <ImageUploader />;
              }
            })()}
          </Suspense>
        </section>

        <section className="max-w-3xl mx-auto mb-8">
          <p className="text-muted-foreground leading-relaxed">
            {tool.introParagraph}
          </p>
        </section>

        {showAds && adPositions.includes('inline') && (
          <Suspense fallback={null}>
            <AdSlot position="inline" className="max-w-3xl mx-auto" />
          </Suspense>
        )}

        <section className="mb-16">
          <ToolExample
            toolExampleType={tool.toolExampleType}
            sourceFormat={sourceFormat}
            targetFormat={targetFormat}
            targetSize={targetSize}
          />
        </section>

        {tool.seoIntro && (
          <section className="max-w-3xl mx-auto mb-16">
            <p className="text-muted-foreground leading-relaxed">
              {tool.seoIntro}
            </p>
          </section>
        )}

        <section id="tool-how-it-works" className="max-w-3xl mx-auto mb-16">
          <h2 className="text-2xl font-bold text-foreground text-center mb-8">
            ¿Cómo funciona?
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {tool.howItWorksSteps.map(step => (
              <div key={step.number} className="text-center p-6 rounded-xl bg-card border border-border">
                <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-lg font-bold mx-auto mb-4">
                  {step.number}
                </div>
                <h3 className="font-semibold text-foreground mb-2">{step.title}</h3>
                <p className="text-sm text-muted-foreground">{step.description}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="max-w-3xl mx-auto mb-16">
          <h2 className="text-2xl font-bold text-foreground text-center mb-8">
            Ventajas
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {tool.benefits.map((benefit, i) => (
              <div key={i} className="flex gap-3 p-4 rounded-lg bg-card border border-border">
                <CheckCircle className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                <div>
                  <h3 className="font-semibold text-foreground mb-1">{benefit.title}</h3>
                  <p className="text-sm text-muted-foreground">{benefit.description}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="max-w-3xl mx-auto mb-16">
          <h2 className="text-2xl font-bold text-foreground text-center mb-8">
            Preguntas frecuentes
          </h2>
          <div className="space-y-4">
            {tool.faqItems.map((faq, i) => (
              <details key={i} className="group p-4 rounded-lg bg-card border border-border">
                <summary className="flex items-center gap-2 cursor-pointer font-semibold text-foreground list-none">
                  <HelpCircle className="h-4 w-4 text-primary shrink-0" />
                  {faq.question}
                </summary>
                <p className="mt-3 text-sm text-muted-foreground pl-6">
                  {faq.answer}
                </p>
              </details>
            ))}
          </div>
        </section>

        <RelatedTools currentSlug={tool.slug} relatedSlugs={tool.relatedSlugs} />

        {showAds && adPositions.includes('inline') && (
          <Suspense fallback={null}>
            <AdSlot position="inline" className="max-w-3xl mx-auto" />
          </Suspense>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default ToolPage;
